import { differenceInHours, startOfDay, subDays } from 'date-fns'
import { defineStore } from 'pinia'
import candidateGateway from '../api/candidateGateway'
import tooManagement from '../api/tooManagement'

import places from '../utils/nl-places.json'

const GEMEENTEBANEN_ID = 10

export const disciplineCategories = {
  'administratief-secretarieel': 'Bestuur & Organisatie',
  'automatisering-ict': 'Operationeel & Facilitair',
  'verkeer-vervoer-nautisch': 'Openbare Orde & Veiligheid',
  bestuurlijk: 'Bestuur & Organisatie',
  'binnendienst-algemene-dienst': 'Operationeel & Facilitair',
  'bouwkunde-civiele-techniek': 'Ruimte & Techniek',
  brandweer: 'Openbare Orde & Veiligheid',
  'buitendienst-groenvoorziening': 'Ruimte & Techniek',
  'burger-publiekszaken': 'Burgerdiensten & Welzijn',
  'dienstverlening-facilitair': 'Operationeel & Facilitair',
  'financieel-economisch': 'Financiën & Recht',
  gezondheidszorg: 'Burgerdiensten & Welzijn',
  griffie: 'Bestuur & Organisatie',
  juridisch: 'Financiën & Recht',
  'onderwijs-wetenschap-cultuur': 'Communicatie & Kennisdeling',
  'openbare-orde-en-veiligheid': 'Openbare Orde & Veiligheid',
  'personeel-organisatie': 'Bestuur & Organisatie',
  'ruimtelijke-ordening-milieu': 'Ruimte & Techniek',
  'sociale-zaken-werkgelegenheid': 'Burgerdiensten & Welzijn',
  'sport-recreatie': 'Burgerdiensten & Welzijn',
  'verkoop-inkoop': 'Financiën & Recht',
  'voorlichting-communicatie': 'Communicatie & Kennisdeling',
  welzijn: 'Burgerdiensten & Welzijn',
}

export const useDashboardStore = defineStore('dashboardStore', {
  state: () => ({
    isLoading: false,
    error: null,
    startDate: null,
    endDate: null,
    vacancies: [],
    candidates: [],
    jobAlerts: [],
  }),
  actions: {
    async fetchVacancies() {
      // Reset state
      this.vacancies = []
      this.error = null

      this.isLoading = true

      this.endDate = new Date()
      this.startDate = startOfDay(subDays(this.endDate, 30))

      const queryParams = {
        siteIds: [GEMEENTEBANEN_ID],
        publication: {
          startsBefore: this.endDate,
          startsAfter: subDays(this.startDate, 30).toISOString(),
          endsBefore: null,
          endsAfter: null,
          scopes: ['EXTERNAL', 'INTERNAL', 'REGIONAL'],
        },
      }

      const fetchVacanciesPage = async (page = 1) => {
        const { data, meta } = await tooManagement.searchVacancies(
          queryParams,
          {
            page,
            size: 200,
          },
        )
        this.vacancies.push(...data)
        if (meta.page.totalPages > page) {
          await fetchVacanciesPage(page + 1)
        }
      }
      await fetchVacanciesPage()

      const currentPeriod = {}
      const previousPeriod = {}
      const companies = {}
      const regions = { 'not-set': 0 }
      const educationLevels = { 'not-set': 0 }
      const disciplines = { 'not-set': 0 }

      let totalPrevious = 0
      let totalCurrent = 0
      const publicationHoursDuration = []

      this.vacancies.forEach((vacancy) => {
        const publication = vacancy.publications.find(
          (pub) => pub.siteId === GEMEENTEBANEN_ID,
        )

        const date = new Date(publication.startDate)
        const offset = date.getTimezoneOffset() // in minutes
        const localDate = new Date(date.getTime() - offset * 60 * 1000)
        const dateKey = localDate.toISOString().split('T')[0]

        if (date < this.startDate) {
          totalPrevious += 1
          if (!previousPeriod[dateKey]) {
            previousPeriod[dateKey] = 0
          }
          previousPeriod[dateKey] += 1
        } else {
          totalCurrent += 1
          if (!currentPeriod[dateKey]) {
            currentPeriod[dateKey] = 0
          }
          currentPeriod[dateKey] += 1

          const endDate = new Date(publication.endDate)
          // We check if it's an open ended vacancy
          if (endDate.getFullYear() < 2900) {
            const duration = differenceInHours(endDate, date)
            publicationHoursDuration.push(duration)
          }

          if (!companies[vacancy.companyName]) {
            companies[vacancy.companyName] = 0
          }
          companies[vacancy.companyName] += 1

          if (!vacancy.regions?.length) {
            regions['not-set'] += 1
          } else {
            vacancy.regions.forEach((region) => {
              if (!regions[region]) {
                regions[region] = 0
              }
              regions[region] += 1
            })
          }

          let hasEducationTags = false
          let hasDisciplineTags = false
          publication.tags.forEach((tag) => {
            if (tag.category === 'VACANCY_EDUCATION') {
              hasEducationTags = true

              if (!educationLevels[tag.key]) {
                educationLevels[tag.key] = 0
              }
              educationLevels[tag.key] += 1
            } else if (tag.category === 'VACANCY_WORKFIELD') {
              hasDisciplineTags = true

              const category = disciplineCategories[tag.key]
              if (!disciplines[category]) {
                disciplines[category] = 0
              }
              disciplines[category] += 1
            }
          })

          if (!hasEducationTags) {
            educationLevels['not-set'] += 1
          }
          if (!hasDisciplineTags) {
            disciplines['not-set'] += 1
          }
        }
      })

      this.isLoading = false
      return {
        currentPeriod,
        previousPeriod,
        companies,
        regions,
        disciplines,
        educationLevels,
        totalCurrent,
        totalPrevious,
        publicationHoursDuration,
      }
    },
    async fetchCandidates() {
      // Reset state
      this.candidates = []
      this.error = null

      this.isLoading = true
      try {
        this.endDate = new Date()
        this.startDate = startOfDay(subDays(this.endDate, 30))

        const queryParams = {
          sortBy: 'applicationDate',
          sortOrder: 'desc',
          size: 200,
          flowTypes: ['vacancy-apply', 'vacancy-soft-apply'],
          startDate: this.startDate.toISOString(),
          endDate: this.endDate.toISOString(),
          excludeTestCandidates: true,
          excludeDuplicateCandidates: false,
          // TODO Add filter for site id!
        }

        const fetchCandidatesPage = async (page = 1) => {
          const { data, meta } = await candidateGateway.getCandidates({
            ...queryParams,
            page,
          })
          this.candidates.push(...data)
          if (meta.page.totalPages > page) {
            await fetchCandidatesPage(page + 1)
          }
        }
        await fetchCandidatesPage()

        const hardApplies = {}
        const softApplies = {}
        this.candidates.forEach((candidate) => {
          const date = new Date(candidate.applicationDate)
          const offset = date.getTimezoneOffset() // in minutes
          const localDate = new Date(date.getTime() - offset * 60 * 1000)
          const dateKey = localDate.toISOString().split('T')[0]

          let appliesObj = hardApplies
          if (candidate.flowTypeSlug === 'vacancy-soft-apply') {
            appliesObj = softApplies
          }

          if (!appliesObj[dateKey]) {
            appliesObj[dateKey] = 0
          }
          appliesObj[dateKey] += 1
        })

        return {
          hardApplies,
          softApplies,
        }
      } catch (error) {
        this.error = error
      } finally {
        this.isLoading = false
      }
    },
    async fetchJobAlerts() {
      // Reset state
      this.jobAlerts = []
      this.error = null

      this.isLoading = true
      try {
        this.endDate = new Date()
        this.startDate = startOfDay(subDays(this.endDate, 30))

        const fetchJobAlertsPage = async (page = 1) => {
          const { data, meta } = await tooManagement.searchJobAlerts(
            {
              createdAfter: subDays(this.startDate, 30).toISOString(), // To include the previous period
              createdBefore: this.endDate.toISOString(),
              siteId: GEMEENTEBANEN_ID,
            },
            {
              page,
              size: 200,
              sortBy: 'id',
              sortOrder: 'desc',
            },
          )
          this.jobAlerts.push(...data)
          if (meta.page.totalPages > page) {
            await fetchJobAlertsPage(page + 1)
          }
        }
        await fetchJobAlertsPage()

        const currentPeriod = {}
        const previousPeriod = {}
        const regions = { 'not-set': 0 }
        const educationLevels = { 'not-set': 0 }
        const disciplines = { 'not-set': 0 }
        const categories = {
          noFilters: 0,
          searchTerm: 0,
          location: 0,
          company: 0,
          education: 0,
          discipline: 0,
          employmentType: 0,
        }

        let totalPrevious = 0
        let totalCurrent = 0

        this.jobAlerts.forEach((jobAlert) => {
          const date = new Date(jobAlert.createdDate)
          const offset = date.getTimezoneOffset() // in minutes
          const localDate = new Date(date.getTime() - offset * 60 * 1000)
          const dateKey = localDate.toISOString().split('T')[0]

          if (date < this.startDate) {
            totalPrevious += 1
            if (!previousPeriod[dateKey]) {
              previousPeriod[dateKey] = 0
            }
            previousPeriod[dateKey] += 1
          } else {
            totalCurrent += 1
            if (!currentPeriod[dateKey]) {
              currentPeriod[dateKey] = 0
            }
            currentPeriod[dateKey] += 1

            const search = jobAlert.search

            let hasLocation = true
            if (!search.region?.length) {
              // If the job alert has a location, we get the region from there
              if (search.latitude && search.longitude) {
                const region = places.find(
                  (place) =>
                    place.lat == search.latitude &&
                    place.lon == search.longitude,
                )
                if (region) {
                  if (!regions[region.province]) {
                    regions[region.province] = 0
                  }
                  regions[region.province] += 1
                  categories.location += 1
                } else {
                  hasLocation = false
                }
              } else {
                hasLocation = false
              }
            } else {
              categories.location += 1
              search.region.forEach((region) => {
                if (!regions[region]) {
                  regions[region] = 0
                }
                regions[region] += 1
              })
            }
            if (!hasLocation) {
              regions['not-set'] += 1
            }

            if (!search.discipline?.length) {
              disciplines['not-set'] += 1
            } else {
              categories.discipline += 1
              search.discipline.forEach((discipline) => {
                const category = disciplineCategories[discipline]
                if (!disciplines[category]) {
                  disciplines[category] = 0
                }
                disciplines[category] += 1
              })
            }

            if (!search.education?.length) {
              educationLevels['not-set'] += 1
            } else {
              categories.education += 1
              search.education.forEach((education) => {
                if (!educationLevels[education]) {
                  educationLevels[education] = 0
                }
                educationLevels[education] += 1
              })
            }

            if (search.employmentType?.length) {
              categories.employmentType += 1
            }

            if (search.companyId) {
              categories.company += 1
            }

            if (search.searchTerm) {
              categories.searchTerm += 1
            }

            if (
              !hasLocation &&
              !search.discipline?.length &&
              !search.education?.length &&
              !search.employmentType?.length &&
              !search.companyId &&
              !search.searchTerm
            ) {
              categories.noFilters += 1
            }
          }
        })

        return {
          currentPeriod,
          previousPeriod,
          regions,
          disciplines,
          educationLevels,
          categories,
          totalCurrent,
          totalPrevious,
        }
      } catch (error) {
        this.error = error
      } finally {
        this.isLoading = false
      }
    },
  },
})
